import React from 'react';
import Page from 'src/components/Page';
import ExpandDoc from 'src/components/ExpandDoc';
import SpecLink from 'src/components/SpecLink';
import { toPublicResource } from '../../../util/pathUtils';

function TxnHppConfigurationPage() {
    return <Page header="HPP Configuration">
        <p>
            Hosted Payment Page or HPP as it is commonly known, allows you to configure a 
            payment link that you can send or route your customers to the page so they can complete their payment.
        </p>
        <div>
            <p>
            Here is an example of what a HPP page looks like when your customer clicks on the HPP link they receive:            
            </p>
            <img src={toPublicResource('/media/images/HPPConfig.png')} alt="Hosted payment page" width="400" height="400" />
            <p>
                There are five sections in a HPP:
                <ul>
                    <li><b>Return bar</b> section - is optional.  It could link back to your shopping site so customers can make additional purchases.</li>
                    <li><b>CRN</b> section - identifies Customer Reference Number assigned to your customer.  There can be up to three CRNs.</li>
                    <li><b>Amount</b> section - identifies amount owned.</li>
                    <li><b>Payment Method</b> section - allows customer to choose their payment method.</li>
                    <li><b>Payment Details</b> section - this section is dependant on the customers Payment method.  In the image above, the payment method is for card details.  
                    If bank account was an option, then BSB, Account number and Name would be displayed.</li>
                </ul>
            </p>
        </div>

        <p>
            Creating a HPP link to send to your customer involves calling the following APIs:
            <ol>
                <li><SpecLink uriFragment="#operation/TxnAuthkey_CreateAuthkey">Create authkey</SpecLink></li>
                <li><SpecLink uriFragment="#operation/TxnAuthkey_AttachTxnDetails">Attach transaction details</SpecLink></li>
                <li><SpecLink uriFragment="#operation/TxnAuthkey_AttachHppConfigurationWithWebhook">Attach HPP Configuration with Webhook</SpecLink></li>
            </ol>             
        </p>

        <h2>Setting up Hosted Payment Page</h2>
        <p>After creating the AuthKey and attaching Transaction Details, you can configure the HPP by calling <SpecLink uriFragment="#operation/TxnAuthkey_AttachHppConfigurationWithWebhook">Attach HPP Configuration with Webhook</SpecLink></p>

        <p>You can set the following options to tailor the HPP page to your requirements</p>
        <ExpandDoc
            method={<SpecLink uriFragment="#operation/TxnAuthkey_AttachHppConfigurationWithWebhook">Attach HPP Configuration with Webhook</SpecLink>}
            doc={{
                authkey: {
                    type: 'string',
                    description: "Transaction authkey"
                },
                options: {
                    description: "These options will determine how your HPP will be configured.",
                    children: {
                        redirectionUrl: {
                            type: "string",
                            description: "Redirection Url is optional and can be used to link back to your shopping site so customers can make additional purchases."
                        },
                        tokeniseTxnCheckBoxDefaultValue: {
                            type: "boolean",
                            description: "Presents a checkbox giving the customer an option to allow their payment details to be tokenised."
                        },
                        hideCRN1: {
                            type: "boolean",
                            description: "Hide CRN1 from being presented on the HPP page."
                        },
                        hideCRN2: {
                            type: "boolean",
                            description: "Hide CRN2 from being presented on the HPP page."
                        },
                        hideCRN3: {
                            type: "boolean",
                            description: "Hide CRN3 from being presented on the HPP page."
                        },
                        hideBillerCode: {
                            type: "boolean",
                            description: "Hide BillerCode from being presented on the HPP page."
                        },
                        returnBarLabel: {
                            type: "string",
                            description: "Return bar label is optional is presented on the HPP page."
                        },
                        returnBarUrl: {
                            type: "boolean",
                            description: "Return bar url is optional and can be used to link back to your shopping site so customers can make additional purchases."
                        },
                        webhook: {
                            description: "Webhook is optional.  It allows you to receive the Txn object once a customer makes their payment.",
                            children: {
                                url: {
                                    type: "string",
                                    description: "Your url which will receive the Txn object for your backend system to process."
                                },
                                version: {
                                    type: "string",
                                    description: "The API version of the webhook response. Defaults to API version of the request."
                                }
                            }
                        }
                    }
                }
            }}
        />

        <p>
            Upon executing <SpecLink uriFragment="#operation/TxnAuthkey_AttachHppConfigurationWithWebhook">Attach HPP Configuration</SpecLink> a specifically configured
            HPP url is returned which you can use to direct your customers to make payments.
        </p>
    </Page>
}

export default TxnHppConfigurationPage;